import React from 'react';
import { Col, Form, FormGroup, FormControl, ControlLabel, HelpBlock, Button, Checkbox } from 'react-bootstrap';
import DatePicker from 'react-bootstrap-date-picker';
import Scroll from 'react-scroll';
import rest from 'rest';

import { REST_API_URL, APP_PATH, PERMISSION_READ } from "../../Utils/Global";
import { getDateFromString, getIsoStringFromDate, getStringFromDate } from "../../Utils/Date";
import { cloneObject } from "../../Utils/Utils";

import PermissionCheck from '../Permissions/PermissionCheck';

import AdvancedCombobox from '../Helper/AdvancedCombobox';
import ControlledGrid from '../Helper/ControlledGrid';
import GeometryGrid from '../Helper/GeometryGrid';
import Grid from '../Helper/Grid';
import Status from '../Helper/Status';

import AbstractEntity from './AbstractEntity';
import Alerte from './Alerte';
import Document from './Document';
import ElementProjet from './ElementProjet';
import Financeur from './Financeur';
import MotCle from './MotCle';
import Prestataire from './Prestataire';
import Responsable from './Responsable';
import Thematique from './Thematique';


class Projet extends AbstractEntity {
  /**
  * props
  * mode : readonly/add/edit
  * path : rest api path for the object's type
  * objectPath : rest api path for the object
  * data.object : instance data
  **/

  /**
  * getClassName
  * Returns the class name as String (once bundled the class name changes)
  **/
  static getClassName() {
    return "Projet";
  }

  /**
  * render
  * Rendering component function : display the view
  **/
  render() {

    if(this.state.object === undefined) {
        return null;
    }

    // Form status information
    const status = <Status parent={this} type={this.state.status.type} message={this.state.status.message} />;

    // Transforms date fields
    let dateDebut = null, dateFin = null;

    if(this.state.object !== undefined) {
      if(this.state.object.dateDebut) {

        dateDebut = this.state.object.dateDebut;

        if(typeof dateDebut === "string") {
          dateDebut = getDateFromString(dateDebut);
        }

        dateDebut = getIsoStringFromDate(dateDebut);
      }

      if(this.state.object.dateFin) {

        dateFin = this.state.object.dateFin;

        if(typeof dateFin === "string") {
          dateFin = getDateFromString(dateFin);
        }

        dateFin = getIsoStringFromDate(dateFin);
      }
    }

    const historyBack = <Button bsStyle="link" onClick={this.props.modal ? this.props.closeModal : this.goBack} className="btnBack"><span className="glyphicon glyphicon-arrow-left" /> Retour</Button>;

    // Rendering the view
		return (
      <div>
        {/* Status information */}
        {status}
        {historyBack}
  			<Form horizontal onSubmit={this.handleSubmit}>
          <HelpBlock>Formulaire du projet.</HelpBlock>

          {/* Form content */}
          <FormGroup controlId="idProjet" className="customHidden">
            <Col componentClass={ControlLabel} sm={2}>Identifiant</Col>
            <Col sm={1}><FormControl type="text" value={this.getObjectProperty("idProjet")} disabled="true" /></Col>
          </FormGroup>

          <FormGroup controlId="estEnvironnemental">
            <Col smOffset={2} sm={4}><Checkbox name="estEnvironnemental" onChange={this.handleCheckboxChange.bind(this)}
              checked={this.getObjectProperty("estEnvironnemental")} disabled={this.state.mode !== "add"}>Est une clause environnementale</Checkbox></Col>
          </FormGroup>

          <FormGroup controlId="libelle">
            <Col componentClass={ControlLabel} sm={2}><ControlLabel>Libellé</ControlLabel></Col>
            <Col componentClass={ControlLabel} sm={6}><FormControl type="text" onChange={this.handleChange} value={this.getObjectProperty("libelle")} disabled={this.getReadOnly()} /></Col>
          </FormGroup>

          <FormGroup controlId="dateDebut">
            <Col componentClass={ControlLabel} sm={2}>Date de début</Col>
            <Col sm={2}><DatePicker id="dateDebut" dateFormat="DD/MM/YYYY" value={dateDebut} onChange={this.handleDateDebutChange} disabled={this.getReadOnly()} /></Col>
            <Col componentClass={ControlLabel} sm={1}>Date de fin</Col>
            <Col sm={2}><DatePicker id="dateFin" minDate={dateDebut} dateFormat="DD/MM/YYYY" value={dateFin} onChange={this.handleDateFinChange} disabled={this.getReadOnly()} /></Col>
          </FormGroup>

          {/* Responsable OneToMany */}
          <PermissionCheck component={Responsable.getClassName()} permission={PERMISSION_READ}>
          <FormGroup controlId="idResponsable">
            <Col componentClass={ControlLabel} sm={2}>Responsable</Col>
            <Col sm={4}><AdvancedCombobox path="responsables" objectPath={this.getObjectPath() + "responsables"} objectClass={Responsable} url={REST_API_URL + "responsables/"} optionValueKey="idResponsable" optionLabelKey="libelleResponsable" default={this.getObjectProperty("idResponsable")} onChange={this.handleResponsableChange} disabled={this.getReadOnly()} enableSearch={true} /></Col>
          </FormGroup>
          </PermissionCheck>

          {/* Financeur ManyToMany */}
          <PermissionCheck component={Financeur.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="financeurs">
            <Col componentClass={ControlLabel} sm={2}>Financeurs</Col>
            <Col sm={6}>
              <ControlledGrid ref="financeursGrid" path="financeurs" objectPath={"projets_financeurs/" + this.getObjectProperty("idProjet") + "/financeurs"}
              objectClass={Financeur} mode={this.state.mode} cbxUrl={REST_API_URL + "projets_financeurs/" + this.getObjectProperty("idProjet") + "/financeurs/inverse/"}
              optionValueKey="idFinanceur" optionLabelKey="libelleFinanceur" />
            </Col>
            </FormGroup>
          </PermissionCheck>

          {/* Prestataire ManyToMany */}
          <PermissionCheck component={Prestataire.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="prestataires">
              <Col componentClass={ControlLabel} sm={2}>Prestataires</Col>
              <Col sm={6}>
                <ControlledGrid ref="prestatairesGrid" path="prestataires" objectPath={"projets_prestataires/" + this.getObjectProperty("idProjet") + "/prestataires"}
                objectClass={Prestataire} mode={this.state.mode} cbxUrl={REST_API_URL + "projets_prestataires/" + this.getObjectProperty("idProjet") + "/prestataires/inverse/"}
                optionValueKey="idPrestataire" optionLabelKey="libelle" />
              </Col>
            </FormGroup>
          </PermissionCheck>

          {/* Thematique ManyToMany */}
          <PermissionCheck component={Thematique.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="thematiques">
            <Col componentClass={ControlLabel} sm={2}>Thématiques</Col>
            <Col sm={6}>
              <ControlledGrid ref="thematiquesGrid" path="thematiques" objectPath={"projets_thematiques/" + this.getObjectProperty("idProjet") + "/thematiques"} objectClass={Thematique}
              mode={this.state.mode} cbxUrl={REST_API_URL + "projets_thematiques/" + this.getObjectProperty("idProjet") + "/thematiques/inverse/"} optionValueKey="idThematique"
              optionLabelKey="libelle" />
            </Col>
            </FormGroup>
          </PermissionCheck>

          {/* MotCle ManyToMany */}
          <PermissionCheck component={MotCle.getClassName()} permission={PERMISSION_READ}>
            <FormGroup controlId="motCles">
            <Col componentClass={ControlLabel} sm={2}>Mots clé</Col>
            <Col sm={6}>
              <ControlledGrid ref="motclesGrid" path="motcles" objectPath={"projets_motcles/" + this.getObjectProperty("idProjet") + "/motcles"} objectClass={MotCle}
              mode={this.state.mode} cbxUrl={REST_API_URL + "projets_motcles/" + this.getObjectProperty("idProjet") + "/motcles/inverse/"} optionValueKey="idMotCle"
              optionLabelKey="libelle" />
            </Col>
            </FormGroup>
          </PermissionCheck>

          <FormGroup controlId="commentaire1">
            <Col componentClass={ControlLabel} sm={2}><ControlLabel>Commentaire</ControlLabel></Col>
            <Col componentClass={ControlLabel} sm={6}><FormControl onChange={this.handleChange} componentClass="textarea" rows="5" value={this.getObjectProperty("commentaire1")} disabled={this.getReadOnly()} /></Col>
          </FormGroup>

          <FormGroup controlId="commentaire2">
            <Col componentClass={ControlLabel} sm={2}><ControlLabel>Commentaire 2</ControlLabel></Col>
            <Col componentClass={ControlLabel} sm={6}><FormControl onChange={this.handleChange} componentClass="textarea" rows="5" value={this.getObjectProperty("commentaire2")} disabled={this.getReadOnly()} /></Col>
          </FormGroup>

          {/* ElementProjet OneToMany */}
          {this.state.mode !== "add" &&
            <PermissionCheck component={ElementProjet.getClassName()} permission={PERMISSION_READ}>
              <Grid title="Eléments de projet" path="elementprojets" objectPath={"projets/" + this.getObjectProperty("idProjet") + "/elementprojets"} objectClass={ElementProjet}
              hiddable={true} mode={this.state.mode} />
            </PermissionCheck>
          }

          {/* Geom OneToMany */}
          {(this.state.mode !== "add") ?
            <GeometryGrid title="Géométries" path="projets_geoms" objectPath={"projets_geoms/" + this.getObjectProperty("idProjet")} objectClass={Projet} mode={this.state.mode}
              queryLayers="ProjetCreate"
              allowDraw={true} selectionMultiple={true} typeGeometry="all" withImportCsv={false} withImportPoint={false} /> : null
          }

          {/* Document OneToMany */}
          {this.state.mode !== "add" &&
            <PermissionCheck component={Document.getClassName()} permission={PERMISSION_READ}>
              <Grid title="Documents" path="documents" objectPath={"projets/" + this.getObjectProperty("idProjet") + "/documents"} objectClass={Document}
              hiddable={true} mode={this.state.mode} />
            </PermissionCheck>
          }

          {/* Alerte OneToMany */}
          {this.state.mode !== "add" &&
            <PermissionCheck component={Alerte.getClassName()} permission={PERMISSION_READ}>
              <Grid title="Alertes" path="alertes" objectPath={"projets/" + this.getObjectProperty("idProjet") + "/alertes"} objectClass={Alerte}
              hiddable={true} mode={this.state.mode} />
            </PermissionCheck>
          }

          {/* Submit button */}
          {this.state.mode !== "readonly" ?
            <FormGroup>
              <Col smOffset={7} sm={1}>
                <Button bsStyle="success" type="submit">Sauvegarder</Button>
              </Col>
            </FormGroup> : ""
          }
  			</Form>

      </div>
		);
	}

  /**
  * getIdField
  * Returns the object idField
  **/
  getIdField() {
    return "idProjet";
  }

  /**
  * sendFormData
  * Saves the data
  **/
  sendFormData () {
    let method;
    const path = REST_API_URL + this.getObjectPath();
    let projetObj = cloneObject(this.state.object);

    if(this.state.mode === "edit") {
      method = "PUT";
    } else if (this.state.mode === "add") {
      method = "POST";
      if(this.refs.prestatairesGrid !== undefined) projetObj.prestataires = this.refs.prestatairesGrid.getArrayToSave();
      if(this.refs.financeursGrid !== undefined) projetObj.financeurs = this.refs.financeursGrid.getArrayToSave();
      if(this.refs.thematiquesGrid !== undefined) projetObj.thematiques = this.refs.thematiquesGrid.getArrayToSave();
      if(this.refs.motclesGrid !== undefined) projetObj.motCles = this.refs.motclesGrid.getArrayToSave();
    }

    if(typeof projetObj.dateDebut !== "string") {
      projetObj.dateDebut = getStringFromDate(projetObj.dateDebut);
    }

    if(typeof projetObj.dateFin !== "string") {
      projetObj.dateFin = getStringFromDate(projetObj.dateFin);
    }

    if(projetObj.estEnvironnemental !== true) projetObj.estEnvironnemental = false;

    Scroll.animateScroll.scrollToTop({
      duration: 1000,
      delay: 100,
      smooth: true
    });

    rest({
      method: method,
      path: path,
      entity: JSON.stringify(projetObj),
      headers: {
  			'Content-Type': 'application/json'
  		}
    }).then(response => {
      if(response.status.code === 200) {
        this.saveGrids();
        this.setState({
          status: {
            type: 'success',
            message: 'Projet sauvegardé.'
          },
          object: JSON.parse(response.entity)
        });
      } else if(response.status.code === 201) {
        const newObj = JSON.parse(response.entity);
        const state = {
          status: {
            type: 'success',
            message: 'Projet sauvegardé.'
          },
          mode: 'edit',
          object: newObj
        };

        this.replaceHistory(APP_PATH + "/projets/" + newObj[this.getIdField()] + "/edit", state);
      } else {
        this.setState({
          status: {
            type: 'danger',
            message: 'Erreur de sauvegarde.'
          }
        });
      }
  	}, response => {
      this.setState({
        status: {
          type: 'danger',
          message: 'Problème d\'appel au serveur.'
        }
      });
		});
  }

  /**
  * saveGrids
  * Saves the ManyToMany components
  **/
  saveGrids() {
    if(this.refs.financeursGrid !== undefined) this.refs.financeursGrid.saveAll();
    if(this.refs.prestatairesGrid !== undefined) this.refs.prestatairesGrid.saveAll();
    if(this.refs.thematiquesGrid !== undefined) this.refs.thematiquesGrid.saveAll();
    if(this.refs.motclesGrid !== undefined) this.refs.motclesGrid.saveAll();
  }

}

export default Projet;



// WEBPACK FOOTER //
// ./src/views/Entity/Projet.js